import { useEffect, useState } from "react";
import { fetchInfo } from "../../lib/hyperliquid";
import { formatPrice, formatPct, formatNumber } from "../../lib/format";

/**
 * Market strip — adapts to single (BTC-USD) and multi (BTC-HYPE ratio) pairs.
 *
 * For multi pairs:
 *  • Mark = live ratio (base_USD / quote_USD)
 *  • 24h Change = ratio_now / ratio_24h_ago - 1  (NOT a single-coin %)
 *  • Oracle = derived ratio from oraclePx of both legs
 *  • Volume = base leg's USD volume (ratio "volume" isn't well-defined)
 *  • Open Interest, Funding, Prev Day  — derived from both legs as ratios
 */
export default function MarketStrip({ coin, quote = "USD", midPrice }) {
    const [baseCtx, setBaseCtx] = useState(null);
    const [quoteCtx, setQuoteCtx] = useState(null);
    const isMulti = quote && quote !== "USD";

    useEffect(() => {
        if (!coin) return;
        let mounted = true;
        const load = async () => {
            try {
                const data = await fetchInfo({ type: "metaAndAssetCtxs" });
                if (!mounted || !Array.isArray(data) || !data[1]) return;
                const universe = data[0]?.universe || [];
                const baseIdx = universe.findIndex((u) => u.name === coin);
                if (baseIdx >= 0) setBaseCtx(data[1][baseIdx]);
                if (isMulti) {
                    const quoteIdx = universe.findIndex((u) => u.name === quote);
                    if (quoteIdx >= 0) setQuoteCtx(data[1][quoteIdx]);
                } else {
                    setQuoteCtx(null);
                }
            } catch {}
        };
        load();
        const id = setInterval(load, 5000);
        return () => { mounted = false; clearInterval(id); };
    }, [coin, quote, isMulti]);

    // ---- derive every metric correctly per pair type ----
    const numOrNull = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) && n !== 0 ? n : null;
    };

    let mark = null, oracle = null, prev = null, change24h = null;
    let baseChg24h = null, quoteChg24h = null;
    let volumeLabel = "—", oiLabel = "—", fundingLabel = "—";

    if (!isMulti && baseCtx) {
        mark = midPrice != null ? +midPrice : numOrNull(baseCtx.markPx);
        oracle = numOrNull(baseCtx.oraclePx);
        prev = numOrNull(baseCtx.prevDayPx);
        if (mark != null && prev != null) change24h = ((mark - prev) / prev) * 100;
        volumeLabel = baseCtx.dayNtlVlm ? "$" + formatNumber(+baseCtx.dayNtlVlm) : "—";
        oiLabel = baseCtx.openInterest ? formatNumber(+baseCtx.openInterest) : "—";
        fundingLabel = baseCtx.funding != null ? formatPct((+baseCtx.funding) * 100, 4) : "—";
    } else if (isMulti && baseCtx && quoteCtx) {
        const bMark = numOrNull(baseCtx.markPx);
        const qMark = numOrNull(quoteCtx.markPx);
        const bOracle = numOrNull(baseCtx.oraclePx);
        const qOracle = numOrNull(quoteCtx.oraclePx);
        const bPrev = numOrNull(baseCtx.prevDayPx);
        const qPrev = numOrNull(quoteCtx.prevDayPx);

        if (midPrice != null) mark = +midPrice;
        else if (bMark != null && qMark != null) mark = bMark / qMark;

        if (bOracle != null && qOracle != null) oracle = bOracle / qOracle;
        if (bPrev != null && qPrev != null) prev = bPrev / qPrev;
        if (mark != null && prev != null) change24h = ((mark - prev) / prev) * 100;

        // Individual 24h moves on each leg — surfaced as a sub-caption so
        // the trader can see WHY the ratio moved.
        if (bMark != null && bPrev != null) baseChg24h = ((bMark - bPrev) / bPrev) * 100;
        if (qMark != null && qPrev != null) quoteChg24h = ((qMark - qPrev) / qPrev) * 100;

        volumeLabel = baseCtx.dayNtlVlm ? "$" + formatNumber(+baseCtx.dayNtlVlm) : "—";
        oiLabel = baseCtx.openInterest ? `${formatNumber(+baseCtx.openInterest)} ${coin}` : "—";
        fundingLabel = baseCtx.funding != null ? formatPct((+baseCtx.funding) * 100, 4) : "—";
    }

    // Pick decimal precision: ratios near 1 need more digits, big BTC mids need fewer.
    const fmt = (n) => {
        if (n == null) return "—";
        if (!Number.isFinite(n)) return "—";
        if (Math.abs(n) >= 1000) return formatPrice(n);
        if (Math.abs(n) >= 1) return n.toFixed(4);
        return n.toFixed(6);
    };

    const changeAccent = change24h == null ? "text-zinc-400" : change24h >= 0 ? "text-emerald-400" : "text-rose-400";
    const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

    const cells = [
        { label: "Mark", value: fmt(mark), accent: "text-cyan-400", testid: "ms-mark" },
        // Special render for 24h Change so multi pairs show the per-leg breakdown.
        { label: "24h Change", testid: "ms-change", custom: true },
        { label: "Oracle", value: fmt(oracle), testid: "ms-oracle" },
        { label: "24h Volume", value: volumeLabel, testid: "ms-vol" },
        { label: isMulti ? "Open Interest (base)" : "Open Interest", value: oiLabel, testid: "ms-oi" },
        { label: isMulti ? "Funding (base)" : "Funding", value: fundingLabel, testid: "ms-funding" },
        { label: "Prev Day", value: fmt(prev), testid: "ms-prev" },
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-1" data-testid="market-strip">
            {cells.map((c) => (
                <div key={c.label} data-testid={c.testid} className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm px-3 py-2.5">
                    <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">{c.label}</div>
                    {c.custom ? (
                        <>
                            <div
                                className={`font-mono text-sm font-medium mt-1 tracking-tight ${changeAccent}`}
                                title={isMulti && baseChg24h != null && quoteChg24h != null
                                    ? `Ratio change = (1 + ${coin}_24h) / (1 + ${quote}_24h) − 1\n= (${(1 + baseChg24h/100).toFixed(4)} / ${(1 + quoteChg24h/100).toFixed(4)}) − 1\n= ${(change24h ?? 0).toFixed(2)}%`
                                    : ""}
                            >
                                {fmtPct(change24h)}
                            </div>
                            {isMulti && (baseChg24h != null || quoteChg24h != null) && (
                                <div className="mt-1 text-[9px] font-mono text-zinc-500 leading-tight">
                                    <span className={baseChg24h == null ? "text-zinc-500" : baseChg24h >= 0 ? "text-emerald-400/80" : "text-rose-400/80"}>
                                        {coin} {fmtPct(baseChg24h)}
                                    </span>
                                    <span className="text-zinc-700 mx-1">·</span>
                                    <span className={quoteChg24h == null ? "text-zinc-500" : quoteChg24h >= 0 ? "text-emerald-400/80" : "text-rose-400/80"}>
                                        {quote} {fmtPct(quoteChg24h)}
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={`font-mono text-sm font-medium mt-1 tracking-tight ${c.accent || "text-zinc-100"}`}>{c.value}</div>
                    )}
                </div>
            ))}
        </div>
    );
}
